import { z } from 'zod';
import { LOCATION_TYPES, PRODUCT_TYPES, STOCK_MOVEMENT_REASONS } from '../constants/order-states.js';
import { UNIT_STATUSES } from '../constants/unit-states.js';
import { e164Schema, moneyCentsSchema, unitIdentifierSchema, usPhoneSchema, uuidSchema, } from './common.js';
export const locationSchema = z.object({
    id: uuidSchema,
    key: z.string().trim().min(2).max(40),
    name: z.string().trim().min(1).max(120),
    type: z.enum(LOCATION_TYPES),
    address: z.string().trim().max(300).nullable(),
    phone: e164Schema.nullable(),
    note: z.string().trim().max(500).nullable(),
    isActive: z.boolean(),
    /**
     * Whether the website may sell what is standing here, and how it reaches
     * the customer. Both off means the location is internal only: its stock is
     * never counted as available online and never sourced by a web order. A
     * repair bench holding a customer's own handset is the reason this exists.
     */
    offersPickup: z.boolean(),
    offersShipping: z.boolean(),
});
export const createLocationSchema = locationSchema.omit({ id: true }).extend({
    address: z.string().trim().max(300).nullable().default(null),
    // Loose input here ("(718) 555-0143") — the read shape above always returns E.164.
    phone: usPhoneSchema.nullable().default(null),
    note: z.string().trim().max(500).nullable().default(null),
    isActive: z.boolean().default(true),
    // Off by default: a new location has to be opened to the website on purpose.
    offersPickup: z.boolean().default(false),
    offersShipping: z.boolean().default(false),
});
/**
 * Key is permanent: the storefront resolves its own location by key.
 *
 * Built from `locationSchema`, not `createLocationSchema` — the create schema's
 * fields carry `.default(null)` for convenience on creation, and `.partial()`
 * on top of a default still fires that default for every field the caller left
 * out. A PATCH sending only `{ name }` would then silently null out the
 * address, phone and note it never touched.
 */
export const updateLocationSchema = locationSchema
    .omit({ id: true, key: true })
    .extend({ phone: usPhoneSchema.nullable() })
    .partial();
/**
 * One scanned row in the intake screen.
 *
 * The IMEI is checked only for shape here, not for its Luhn digit. The intake
 * endpoint's contract is that a batch of fifty scans is accepted and the bad
 * rows are reported individually — rejecting the whole request because one
 * scan misread would mean re-scanning every phone on the bench. The service
 * validates each row properly and returns the failures with reasons.
 */
export const intakeUnitSchema = z.object({
    imei: z.string().trim().min(1).max(64),
    serialNumber: z.string().trim().max(64).nullable().default(null),
    /** The receiver looked at the printed card and accepted an ICCID whose
     *  check digit fails — some carriers print non-Luhn ICCIDs (t0918). Only
     *  honored server-side for a checksum-only ICCID failure; never for a
     *  wrong length, a non-digit, or any IMEI. */
    acceptBadChecksum: z.boolean().default(false),
});
/**
 * Batch intake: the scan loop commits many units at once against a single
 * purchase lot, so cost and vendor are captured once rather than per row.
 */
export const receiveUnitsSchema = z.object({
    productId: uuidSchema,
    locationId: uuidSchema,
    vendor: z.string().trim().max(120).nullable().default(null),
    purchaseOrderRef: z.string().trim().max(64).nullable().default(null),
    unitCostCents: moneyCentsSchema.nullable().default(null),
    units: z.array(intakeUnitSchema).min(1).max(500),
    note: z.string().trim().max(500).nullable().default(null),
});
export const receiveUnitsResultSchema = z.object({
    created: z.array(z.object({ id: uuidSchema, imei: z.string() })),
    rejected: z.array(z.object({ imei: z.string(), reason: z.string() })),
});
/**
 * Per-unit facts that only become known/true at the provisioning or
 * verification stage of this specific device's lifecycle — e.g. a phone
 * stocked "unfiltered" gets filtered during processing and becomes "talk
 * only, no text". Distinct from `products.attributes`, which describes the
 * model, not the individual handset.
 */
export const unitAttributesSchema = z.record(z.string(), z.union([z.string(), z.number(), z.boolean()]));
/** Write shape: full replacement, with sanity limits the read shape doesn't need. */
export const updateUnitAttributesSchema = z.object({
    attributes: z
        .record(z.string().trim().min(1).max(40), z.union([z.string().trim().max(120), z.number(), z.boolean()]))
        .refine((v) => Object.keys(v).length <= 30, 'No more than 30 attributes per device'),
});
export const unitSchema = z.object({
    id: uuidSchema,
    productId: uuidSchema,
    productName: z.string(),
    /** Drives whether the admin shows this row's identifier as an IMEI or an
     *  ICCID — see `unitIdentifierKind`. */
    productType: z.enum(PRODUCT_TYPES),
    imei: z.string(),
    serialNumber: z.string().nullable(),
    status: z.enum(UNIT_STATUSES),
    locationId: uuidSchema.nullable(),
    locationName: z.string().nullable(),
    costCents: z.number().int().nullable(),
    vendor: z.string().nullable(),
    purchaseOrderRef: z.string().nullable(),
    soldOrderId: uuidSchema.nullable(),
    assignedStaffId: uuidSchema.nullable(),
    statusChangedAt: z.iso.datetime(),
    createdAt: z.iso.datetime(),
    attributes: unitAttributesSchema,
    /** The full price this device sells for because of its attributes (t1158),
     *  or null when none applies and the model's normal price is used. */
    attributePriceCents: z.number().int().nonnegative().nullable(),
});
/**
 * Corrects a unit's identifier after it has been received — a mis-scanned
 * digit caught too late for the intake screen's "×"/Clear. Unlike batch
 * intake, this is a single record with a human waiting on the response, so
 * the strict schema (checksum included) applies at the boundary instead of
 * the loose per-row shape `receiveUnitsSchema` uses. Accepts either an IMEI
 * or an ICCID here; the service checks the value against the unit's own
 * product type, since only that knows which one is actually expected.
 */
export const updateUnitSchema = z.object({ imei: unitIdentifierSchema });
/**
 * t1402: whether a filter was put on the device before a forward stage move,
 * and which kind. `installed: false` is the explicit "no filter" answer.
 */
export const unitFilterAnswerSchema = z.discriminatedUnion('installed', [
    z.object({ installed: z.literal(false) }),
    z.object({ installed: z.literal(true), kind: z.string().trim().min(1).max(60) }),
]);
export const transitionUnitSchema = z.object({
    to: z.enum(UNIT_STATUSES),
    note: z.string().trim().max(500).nullable().default(null),
    locationId: uuidSchema.nullable().default(null),
    filter: unitFilterAnswerSchema.nullable().default(null),
});
/** Bulk stage advance from the provisioning queue. */
export const bulkTransitionUnitsSchema = z.object({
    unitIds: z.array(uuidSchema).min(1).max(200),
    to: z.enum(UNIT_STATUSES),
    note: z.string().trim().max(500).nullable().default(null),
    filter: unitFilterAnswerSchema.nullable().default(null),
});
export const bulkTransitionResultSchema = z.object({
    updated: z.array(uuidSchema),
    failed: z.array(z.object({ unitId: uuidSchema, reason: z.string() })),
});
export const unitListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
    q: z.string().trim().max(200).optional(),
    status: z.enum(UNIT_STATUSES).optional(),
    productId: uuidSchema.optional(),
    locationId: uuidSchema.optional(),
    /** true → only units not loaded into a vending slot; false → only ones that are. */
    unassigned: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
        .optional(),
    sort: z.enum(['createdAt', 'statusChangedAt', 'imei']).default('statusChangedAt'),
    order: z.enum(['asc', 'desc']).default('desc'),
});
export const unitHistoryEntrySchema = z.object({
    id: uuidSchema,
    fromStatus: z.enum(UNIT_STATUSES).nullable(),
    toStatus: z.enum(UNIT_STATUSES),
    actorType: z.enum(['staff', 'customer', 'kiosk', 'system']),
    actorLabel: z.string().nullable(),
    note: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
export const stockLevelSchema = z.object({
    productId: uuidSchema,
    productName: z.string(),
    sku: z.string(),
    locationId: uuidSchema,
    locationName: z.string(),
    qty: z.number().int(),
    reserved: z.number().int(),
    available: z.number().int(),
    reorderPoint: z.number().int().nullable(),
    /** The threshold actually in force: reorderPoint, or the store default when null. */
    effectiveReorderPoint: z.number().int(),
});
/** Per-location availability for one product — how much of it can be sold from where. */
export const productAvailabilitySchema = z.object({
    locationId: uuidSchema,
    locationName: z.string(),
    available: z.number().int(),
});
/**
 * Manual adjustment. A reason is mandatory: an unexplained stock change is the
 * one thing an inventory system must never allow.
 */
export const adjustStockSchema = z
    .object({
    productId: uuidSchema,
    locationId: uuidSchema,
    qtyDelta: z
        .number()
        .int()
        .refine((v) => v !== 0, 'Adjustment cannot be zero'),
    // `vendor_return` is written only by opening a vendor return (t1154); allowing
    // it here would take stock off the shelf with no return behind it.
    reason: z.enum(STOCK_MOVEMENT_REASONS).exclude(['vendor_return']),
    note: z.string().trim().min(3, 'Explain why this adjustment is being made').max(500),
    /** The scrapped device these parts were pulled from. Required (and only
     *  meaningful) when `reason` is 'salvage' — see `service.ts#adjustStock`
     *  for the check that the device is actually scrapped. */
    sourceUnitId: uuidSchema.optional(),
    /** `office_use` only (t1223): the expense category (default Office supplies) and the
     *  amount to expense, which defaults to quantity × the product's cost on file. */
    expenseCategoryId: uuidSchema.optional(),
    expenseAmountCents: z.number().int().positive().optional(),
})
    .superRefine((v, ctx) => {
    if (v.reason === 'office_use') {
        if (v.qtyDelta >= 0) {
            ctx.addIssue({
                code: 'custom',
                path: ['qtyDelta'],
                message: 'Office use takes stock out — the change must be negative',
            });
        }
    }
    else if (v.expenseCategoryId || v.expenseAmountCents) {
        ctx.addIssue({
            code: 'custom',
            path: ['expenseCategoryId'],
            message: 'An expense only applies to an office use adjustment',
        });
    }
    if (v.reason === 'salvage') {
        if (!v.sourceUnitId) {
            ctx.addIssue({
                code: 'custom',
                path: ['sourceUnitId'],
                message: 'Choose which device these parts were salvaged from',
            });
        }
        if (v.qtyDelta <= 0) {
            ctx.addIssue({
                code: 'custom',
                path: ['qtyDelta'],
                message: 'Salvaging adds parts to stock — the change must be positive',
            });
        }
    }
    else if (v.sourceUnitId) {
        ctx.addIssue({
            code: 'custom',
            path: ['sourceUnitId'],
            message: 'A source device only applies to a salvage adjustment',
        });
    }
});
export const transferStockSchema = z.object({
    productId: uuidSchema,
    fromLocationId: uuidSchema,
    toLocationId: uuidSchema,
    qty: z.number().int().positive(),
    note: z.string().trim().max(500).nullable().default(null),
});
export const stockMovementSchema = z.object({
    id: uuidSchema,
    productId: uuidSchema,
    productName: z.string(),
    locationId: uuidSchema,
    locationName: z.string(),
    qtyDelta: z.number().int(),
    reason: z.enum(STOCK_MOVEMENT_REASONS),
    refType: z.string().nullable(),
    refId: uuidSchema.nullable(),
    /** The donor device's IMEI, when this movement's `refType` is
     *  'serialized_unit' (a salvage) — null for every other reason, and for a
     *  salvage whose unit was since deleted. */
    refImei: z.string().nullable(),
    actorLabel: z.string().nullable(),
    note: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
export const stockMovementQuerySchema = z.object({
    cursor: z.string().max(100).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    productId: uuidSchema.optional(),
    locationId: uuidSchema.optional(),
    reason: z.enum(STOCK_MOVEMENT_REASONS).optional(),
});
//# sourceMappingURL=inventory.js.map